import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';

import { ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { useDebouncedCallback } from 'use-debounce';

import { currencyFormatter } from '@/utils/currencyFormatter';
import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { ScheduledReductionBanner } from './ScheduledReductionBanner';

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

// Stripe caps invoice amounts at 999,999 (in major units). A 6-digit ceiling on
// the seat count keeps any imaginable per-seat price × seat count under that cap.
const MAX_SEATS = 999_999;

/**
 * Throwaway proration math — the real number must come from Stripe via
 * `Invoice.upcoming` (see Rails callsites: `PlanPriceChangeController`,
 * `Stripe::UpdateSubscriptionPrice`). When BEE-18935 lands a `seat_change_preview`
 * endpoint, swap `computeProratedCents` for `useSeatChangePreview(groupId, target)`
 * so the displayed estimate equals the actual charge by construction.
 *
 * The index payload only carries `renews_at`, so we approximate the period start
 * as `renews_at - 1 year`. Estimate-only.
 */
const computeProratedCents = (group: GroupSubscriptionData, delta: number): number => {
  if (delta <= 0) return 0;
  if (!group.renews_at) return 0;
  const end = new Date(group.renews_at).getTime();
  const start = end - YEAR_MS;
  const now = Date.now();
  const totalMs = end - start;
  const remainingMs = Math.max(0, Math.min(end - now, totalMs));
  if (totalMs <= 0) return 0;
  const fraction = remainingMs / totalMs;
  return Math.round(group.per_seat_amount_cents * delta * fraction);
};

/**
 * "Active assigned" = everything occupying or reserving a seat at renewal,
 * mirroring Rails' `GroupSubscriptionSeat::ACTIVE_STATUSES` (filled + terminating
 * + invited). This is the count the reduction deficit subtracts the target from,
 * so it MUST match the backend or the picker asks for the wrong number.
 *
 * Pending invites are included because they reserve a renewal seat: 8 filled +
 * 1 invited renewing to 5 means 4 filled must terminate (not 3) — the invite
 * still claims one of the 5. Invited seats themselves can't be terminated here
 * (the admin cancels the invite instead), they just count toward the target.
 *
 * Uses the API count fields, not the seats array, since the controlled host
 * paginates/empties `group.seats`.
 */
const countActiveAssigned = (group: GroupSubscriptionData): number => {
  return group.seats_filled + group.seats_terminating + group.invites_pending;
};

type ManageSeatsModalProps = {
  isOpen: boolean;
  group: GroupSubscriptionData | null;
  onCancel: () => void;
  onSubmit?: (groupId: string, targetSeatCount: number) => void;
  /**
   * Reducing below the filled count can't be confirmed inline — the admin must
   * pick which members lose access at renewal. Fired instead of `onSubmit` in
   * that case; `deficit` is how many members must be selected.
   */
  onSelectMembersToRemove?: (groupId: string, targetSeatCount: number, deficit: number) => void;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
};

export function ManageSeatsModal({
  isOpen,
  group,
  onCancel,
  onSubmit,
  onSelectMembersToRemove,
  onOpenChange,
  isLoading = false,
}: ManageSeatsModalProps) {
  // Two-state input: `inputValue` is what the user is typing (string, bound to the
  // input); `committedSeats` is what the math layer reads. Committing happens on
  // blur (immediate) or after a 500ms pause (debounced) so the panels don't flicker
  // mid-keystroke. Lazy init seeds from group.max_seats on mount — the parent
  // unmounts/remounts the modal per-open, so we'll always see fresh max_seats on
  // open, and a background refetch mid-edit won't clobber the user's typing.
  const [inputValue, setInputValue] = useState<string>(() => (group ? String(group.max_seats) : ''));
  const [committedSeats, setCommittedSeats] = useState<number>(() => group?.max_seats ?? 0);

  // Strip non-digits + cap at MAX_SEATS so the input can never represent a value
  // Stripe would reject downstream.
  const handleInputChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits === '') {
      setInputValue('');
      return;
    }
    const n = Math.min(Number.parseInt(digits, 10), MAX_SEATS);
    setInputValue(String(n));
  }, []);

  // Commit raw input → committedSeats. Used both as the debounced target (during
  // typing) and as the immediate commit (on blur via commitNow).
  const commitValue = useCallback((value: string) => {
    if (value === '') return;
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) setCommittedSeats(parsed);
  }, []);

  // Debounced commit so derived panels don't flash on every keystroke.
  const debouncedCommit = useDebouncedCallback(commitValue, 500);

  useEffect(() => {
    debouncedCommit(inputValue);
  }, [inputValue, debouncedCommit]);

  // On blur: cancel any pending debounce and commit immediately so the panels
  // are correct before the user reaches for the submit button.
  const commitNow = useCallback(() => {
    debouncedCommit.cancel();
    commitValue(inputValue);
  }, [commitValue, debouncedCommit, inputValue]);

  const targetSeats = group ? committedSeats : 0;

  // Submit guards key off the *pending* input so the user doesn't have to wait the
  // debounce out before they can confirm — but an empty input must never resolve
  // to "re-submit the previously committed value", so we treat empty as 0 here.
  const pendingTarget = useMemo(() => {
    if (!group) return 0;
    if (inputValue === '') return 0;
    const parsed = Number.parseInt(inputValue, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [group, inputValue]);

  const derived = useMemo(() => {
    if (!group) return null;
    const delta = targetSeats - group.max_seats;
    const isAdding = delta > 0;
    const isRemoving = delta < 0;
    const isUnchanged = delta === 0;
    const activeFilled = countActiveAssigned(group);
    // Reducing below the active-filled count means real members lose access — the
    // admin must pick which ones at renewal, so we hand off to the picker rather
    // than confirm inline. `deficit` = how many must be selected.
    const needsPicker = isRemoving && targetSeats < activeFilled;
    const deficit = needsPicker ? activeFilled - targetSeats : 0;
    const newAnnualCents = targetSeats * group.per_seat_amount_cents;
    const proratedCents = computeProratedCents(group, delta);
    return {
      delta,
      absDelta: Math.abs(delta),
      isAdding,
      isRemoving,
      isUnchanged,
      activeFilled,
      needsPicker,
      deficit,
      newAnnualCents,
      proratedCents,
    };
  }, [group, targetSeats]);

  // Close on ESC. Outside-click is intentionally NOT bound — admins shouldn't lose
  // a half-typed seat count by clicking the overlay.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onOpenChange]);

  if (!isOpen || !group || !derived || !group.renews_at) return null;

  const formatMoney = (cents: number) => currencyFormatter({ value: cents / 100, currency: group.currency });
  // Force UTC — renews_at is a calendar date stored as UTC midnight.
  const renewsOnLabel = dateFormatter({ timeZone: 'UTC' }).format(new Date(group.renews_at));
  const pendingDelta = pendingTarget - group.max_seats;
  const isPendingReduction = inputValue !== '' && pendingDelta < 0;
  const pendingNeedsPicker = isPendingReduction && pendingTarget < derived.activeFilled;
  const pendingDeficit = pendingNeedsPicker ? derived.activeFilled - pendingTarget : 0;
  // Backend floor: a scheduled reduction must keep at least 2 seats.
  const reductionTargetValid = pendingTarget >= 2;
  const canSelectMembers = pendingNeedsPicker && reductionTargetValid && pendingDeficit > 0;
  const canConfirm =
    inputValue !== '' &&
    pendingDelta !== 0 &&
    !pendingNeedsPicker &&
    pendingTarget > 0 &&
    (isPendingReduction ? reductionTargetValid : true);

  const overlayStyle: CSSProperties = { backgroundColor: '#00000030' };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="manage-seats-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={overlayStyle}
    >
      <div className="flex w-full flex-col gap-6 overflow-hidden bg-white p-6 shadow-lg sm:w-[600px] sm:rounded-md">
        <div className="flex flex-col gap-2">
          <h1 id="manage-seats-title" className="text-lg font-medium text-gray-900">
            Manage seats
          </h1>
          <p className="text-sm text-gray-500">
            Change the number of seats in this group subscription. Add seats to give more readers access right away, or
            remove unused seats starting from your next renewal.
          </p>
        </div>

        {/* Surfaces an already-scheduled renewal reduction so the admin sees the
            modal seeds from the purchased total, not the reduced renewal count
            (BEE-20132). Self-guards: renders null when nothing is pending. */}
        <ScheduledReductionBanner group={group} />

        <div className="flex flex-col gap-2">
          <label htmlFor="manage-seats-input" className="text-sm font-medium text-gray-900">
            Number of seats
          </label>
          <div className="flex w-full overflow-hidden border border-gray-200 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-wt-primary">
            <input
              id="manage-seats-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={commitNow}
              className="flex-1 min-w-0 px-3 py-2 text-sm font-normal leading-5 text-gray-900 bg-white border-0 outline-0 focus:text-sm"
            />
            <span className="flex items-center px-3 text-sm text-gray-500 border-l border-gray-200 shrink-0 bg-gray-50">
              Seats
            </span>
          </div>
        </div>

        {!derived.isUnchanged && (
          <div className="p-4 text-sm rounded-lg bg-gray-50">
            {/* gap-4 keeps a minimum 16px gutter between label and price even
                when the label wraps; items-start handles the multi-line "Annual
                total starting next renewal on …" row without odd vertical drift.
                whitespace-nowrap + shrink-0 on each <dd> keeps prices on one line. */}
            <dl className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-4 text-gray-500">
                <dt>Seats {derived.isAdding ? 'added' : 'removed'}</dt>
                <dd className="whitespace-nowrap shrink-0">
                  {derived.absDelta} {derived.absDelta === 1 ? 'seat' : 'seats'}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4 text-gray-500">
                <dt>Total seats</dt>
                <dd className="whitespace-nowrap shrink-0">
                  {targetSeats} {targetSeats === 1 ? 'seat' : 'seats'}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4 text-gray-500">
                <dt>Price per seat</dt>
                <dd className="whitespace-nowrap shrink-0">
                  {formatMoney(group.per_seat_amount_cents)}/{group.interval}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4 pt-4 mt-1 border-t border-gray-200">
                <dt className="font-medium text-gray-900">Annual total starting next renewal on {renewsOnLabel}</dt>
                <dd className="text-lg font-medium text-gray-900 whitespace-nowrap shrink-0">
                  {formatMoney(derived.newAnnualCents)}/{group.interval}
                </dd>
              </div>
              {derived.isAdding && (
                <div className="flex items-start justify-between gap-4">
                  <dt className="font-medium text-gray-900">Prorated charge today (estimate)</dt>
                  <dd className="text-lg font-medium text-gray-900 whitespace-nowrap shrink-0">
                    {formatMoney(derived.proratedCents)}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {derived.isAdding && (
          <div className="flex gap-3 p-3 text-sm text-indigo-900 border border-indigo-200 rounded-md bg-indigo-50">
            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500" />
            <div className="flex flex-col gap-1">
              <p className="font-medium">
                Adding {derived.absDelta} {derived.absDelta === 1 ? 'seat' : 'seats'}
              </p>
              <p>
                This change takes effect immediately. A prorated charge for the rest of your current billing period will
                be applied today.
              </p>
            </div>
          </div>
        )}

        {derived.isRemoving && !derived.needsPicker && (
          <div className="flex gap-3 p-3 text-sm border rounded-md border-amber-200 bg-amber-50 text-amber-900">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div className="flex flex-col gap-1">
              <p className="font-medium">
                Removing {derived.absDelta} {derived.absDelta === 1 ? 'seat' : 'seats'}
              </p>
              <p>
                This change will take effect on your next renewal. You&rsquo;ll keep your current {group.max_seats}{' '}
                seats until then, and your subscription will renew with {targetSeats} seats. Refunds won&rsquo;t be
                issued for the current billing period.
              </p>
            </div>
          </div>
        )}

        {derived.needsPicker && (
          <div className="flex gap-3 p-3 text-sm text-red-900 border border-red-200 rounded-md bg-red-50">
            <XCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <div className="flex flex-col gap-1">
              <p className="font-medium">
                {derived.deficit} {derived.deficit === 1 ? 'member needs' : 'members need'} to be removed at renewal
              </p>
              <p>
                {derived.activeFilled} {derived.activeFilled === 1 ? 'seat is' : 'seats are'} currently filled, but your
                subscription will renew with {targetSeats} seats. Select {derived.deficit}{' '}
                {derived.deficit === 1 ? 'member' : 'members'} to remove at renewal date to continue.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={isLoading}
            onClick={onCancel}
            className="px-3 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          {/* Submit button reads the immediate `pending*` clock (not debounced
              `derived.*`): its enable guard + onClick already use pending values,
              so the label must agree — otherwise "Confirm changes" shows disabled
              for ~500ms before flipping to "Select members". The info panels above
              stay on `derived.*` so they don't flash mid-keystroke. */}
          {pendingNeedsPicker ? (
            <button
              type="button"
              disabled={isLoading || !canSelectMembers}
              onClick={() => onSelectMembersToRemove?.(group.id, pendingTarget, pendingDeficit)}
              className="rounded-md bg-wt-primary px-3 py-2 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:opacity-100"
            >
              Select members
            </button>
          ) : (
            <button
              type="button"
              disabled={isLoading || !canConfirm}
              onClick={() => onSubmit?.(group.id, pendingTarget)}
              className="rounded-md bg-wt-primary px-3 py-2 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:opacity-100"
            >
              Confirm changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
